import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plane, FileText, Star, Compass, User } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useClientAuth } from '../context/ClientAuthContext';
import { GOLD, NAVY, alpha } from '../theme/palette';
const CREAM = '#fcf7ec';
const t = (lang, pt, en, es) => (lang === 'pt' ? pt : lang === 'en' ? en : es);

/**
 * ClientBottomTabBar
 *
 * Fixed bottom navigation shown only on mobile (<640px). Replaces the top
 * ClientNav strip on narrow screens so the top of the viewport stays
 * dedicated to content, matching native iOS/Android apps.
 *
 * Keeps the existing visual identity (NAVY / GOLD / CREAM, uppercase
 * tracking, Cormorant-adjacent spacing). Pages that render the client
 * portal already get `pb-24` on mobile (via the shared wrapper in App.js)
 * so content never hides behind the bar.
 */
const ClientBottomTabBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const { client } = useClientAuth() || {};

  // Lightweight pending-docs badge. We count documents awaiting the
  // client's response from the GET /api/client/documents endpoint. Kept
  // optional — if it fails we simply don't show the badge.
  const [pendingDocs, setPendingDocs] = useState(0);
  useEffect(() => {
    const token = localStorage.getItem('client-token');
    if (!token) return;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/client/documents`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(data => {
        if (!data || !Array.isArray(data)) return;
        const pending = data.filter(d => (d.status || '').toLowerCase() === 'pending').length;
        setPendingDocs(pending);
      })
      .catch(() => {});
  }, [client?.id, location.pathname]);

  const items = [
    {
      path: '/minhas-viagens',
      label: t(language, 'Viagens', 'Trips', 'Viajes'),
      icon: Plane,
      match: (p) => p.startsWith('/minhas-viagens') || p.startsWith('/my-bookings'),
    },
    {
      path: '/my-documents',
      label: t(language, 'Docs', 'Docs', 'Docs'),
      icon: FileText,
      match: (p) => p.startsWith('/my-documents'),
      badge: pendingDocs,
    },
    {
      path: '/depoimentos',
      label: t(language, 'Avaliar', 'Reviews', 'Reseñas'),
      icon: Star,
      match: (p) => p.startsWith('/depoimentos') || p.startsWith('/testimonials'),
    },
    {
      path: '/recursos',
      label: t(language, 'Recursos', 'Resources', 'Recursos'),
      icon: Compass,
      match: (p) => p.startsWith('/recursos'),
    },
    {
      path: '/meu-perfil',
      label: t(language, 'Perfil', 'Profile', 'Perfil'),
      icon: User,
      match: (p) => p.startsWith('/meu-perfil'),
    },
  ];

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-40 sm:hidden"
      style={{
        background: CREAM,
        borderTop: `1px solid ${alpha(GOLD, '66')}`,
        boxShadow: '0 -6px 24px rgba(0,0,0,0.06)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
      data-testid="client-bottom-tab-bar"
    >
      <div className="flex justify-around items-stretch px-1 pt-1.5 pb-1">
        {items.map((item) => {
          const Icon = item.icon;
          const active = item.match(location.pathname);
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className="relative flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 transition"
              style={{ color: NAVY }}
              data-testid={`bottom-tab-${item.path.replace(/\//g, '-').replace(/^-/, '')}`}
            >
              {active && (
                <span
                  aria-hidden
                  className="absolute top-0 left-1/4 right-1/4 h-[3px] rounded-b"
                  style={{ background: GOLD }}
                />
              )}
              <div className="relative">
                <Icon
                  className="w-5 h-5 transition-transform"
                  style={{
                    color: active ? GOLD : NAVY,
                    opacity: active ? 1 : 0.5,
                    transform: active ? 'scale(1.15)' : 'scale(1)',
                  }}
                />
                {item.badge > 0 && (
                  <span
                    className="absolute -top-1 -right-2 min-w-[16px] h-[16px] px-1 flex items-center justify-center rounded-full text-[10px] font-bold text-white"
                    style={{ background: '#e74c3c' }}
                    data-testid={`bottom-tab-badge-${item.path.replace(/\//g, '-').replace(/^-/, '')}`}
                  >
                    {item.badge > 9 ? '9+' : item.badge}
                  </span>
                )}
              </div>
              <span
                className="text-[9px] tracking-[0.15em] uppercase whitespace-nowrap"
                style={{
                  color: NAVY,
                  opacity: active ? 1 : 0.65,
                  fontWeight: active ? 700 : 500,
                }}
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default ClientBottomTabBar;
